import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
  Link,
} from 'react-router-dom';
import { ExitToApp } from '@mui/icons-material';
import {
  AppConfig,
  AppConstants,
  AppRequests,
  createAppConfig,
} from '@ltvco/refresh-lib/ctx';
import { ResponseError } from '@ltvco/refresh-lib/utils';
import { AlertProvider, SideMenuItem } from '@ltvco/refresh-lib/v1';
import { styled } from '@ltvco/refresh-lib/theme';
import { appRequests } from 'appRequests';
import {
  constants,
  searchTabsForNewDesign,
  sideMenuItemsForNewDesign,
} from 'appConstants';

const MenuItem = styled(Link)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: theme.typography.fontSize * 1.125,
  display: 'flex',
  alignItems: 'center',
  svg: {
    marginRight: theme.spacing(1),
  },
}));

export function LibContextWrapper({
  children,
  trackEventGA4,
  trackEvent,
  logError,
  handleLogoutClick,
}: {
  children: React.ReactNode | React.ReactNode[];
  trackEventGA4: (eventToTrack: object, eventName?: string) => void;
  trackEvent: (category: string, action: string, label?: string) => void;
  logError: (
    context: string,
    error: Error | ResponseError,
    url: string | undefined
  ) => void;
  handleLogoutClick: () => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const defaultConfig = createAppConfig();

  function flattenMenuItems(menuItems: SideMenuItem[]): SideMenuItem[] {
    return menuItems.flatMap(({ childItems, ...item }) => [
      item,
      ...(childItems?.length ? flattenMenuItems(childItems) : []),
    ]);
  }

  const flatMenu = flattenMenuItems(
    constants.config.sideMenuItems.filter((item) => {
      return item.name !== 'importList';
    })
  );

  const menuItems = flatMenu.map((item) => {
    return (
      <MenuItem
        to={item.route}
        data-cy={`menu_${item['data-cy']}`}
        target={item.openInNewTab ? '_blank' : ''}
        rel={item.openInNewTab ? 'noopener noreferrer' : ''}
      >
        {item.icon} {item.text}
      </MenuItem>
    );
  });

  function trackListhub(eventName: string, listingKey: string) {
    const listhubFn = (window as any).lh;
    if (listhubFn && typeof listhubFn !== 'undefined') {
      if (eventName && listingKey) {
        listhubFn('submit', eventName || null, { lkey: listingKey || null });
      }
    }
  }

  const appConfig = {
    ...defaultConfig,
    routingUtils: {
      ...defaultConfig.routingUtils,
      navigate: navigate,
      location: location,
      useSearchParams,
      useParams,
    },
    logError: logError,
    trackEvent: trackEvent,
    trackEventGA4: trackEventGA4,
    trackListhub: trackListhub,
  };

  const logout = {
    route: '',
    icon: '',
    text: 'Logout',
    name: 'logout',
    state: '',
    'data-cy': 'menu_log_out_btn',
    action: handleLogoutClick,
  };

  const appConstants = {
    ...constants,
    config: {
      ...constants.config,
      menuItems: [
        ...menuItems,
        <MenuItem
          to="#"
          onClick={() => handleLogoutClick()}
          data-cy="menu_log_out_btn"
        >
          <ExitToApp sx={{ color: '#212121' }} /> Log Out
        </MenuItem>,
      ],
      customSideMenuItems: [...constants.config.sideMenuItems, logout],
      sideMenuItems: [...sideMenuItemsForNewDesign, logout],
      searchTabs: searchTabsForNewDesign,
    },
  };

  return (
    <AlertProvider maxSnack={3} preventDuplicate autoHideDuration={5000}>
      <AppRequests.Provider value={appRequests}>
        <AppConfig.Provider value={appConfig as any}>
          <AppConstants.Provider value={appConstants}>
            {children}
          </AppConstants.Provider>
        </AppConfig.Provider>
      </AppRequests.Provider>
    </AlertProvider>
  );
}
